import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, alpha } from "@mui/material";
import color from "../../../Constants/colors";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import { formatDateTime } from "./format";

export interface TimelineEntry {
  id: number;
  label: string;
  created_at: string;
  note?: string;
  /** Extra line, e.g. who did it or "internal only". */
  meta?: React.ReactNode;
  muted?: boolean;
}

/** History of a deal, newest first, as a vertical line of dots. */
const DealTimeline: React.FC<{
  entries: TimelineEntry[];
  emptyText: string;
}> = ({ entries, emptyText }) => {
  const { i18n } = useTranslation();
  const { accent } = useDashboardColors();

  if (!entries.length) {
    return (
      <Typography sx={{ fontSize: 14, color: color.gray500 }}>
        {emptyText}
      </Typography>
    );
  }

  return (
    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {[...entries].reverse().map((entry, index, list) => (
        <Box
          component="li"
          key={entry.id}
          sx={{
            display: "flex",
            gap: 1.5,
            position: "relative",
            pb: index < list.length - 1 ? 2 : 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: "5px",
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                flexShrink: 0,
                backgroundColor: entry.muted ? color.gray300 : accent,
                boxShadow: `0 0 0 3px ${entry.muted ? color.gray100 : alpha(accent, 0.18)}`,
              }}
            />
            {index < list.length - 1 && (
              <Box
                sx={{
                  width: 2,
                  flex: 1,
                  mt: 0.75,
                  backgroundColor: color.gray200,
                }}
              />
            )}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 14, fontWeight: 700, color: color.gray900 }}
            >
              {entry.label}
            </Typography>
            <Typography sx={{ fontSize: 12, color: color.gray500 }}>
              {formatDateTime(entry.created_at, i18n.language)}
              {entry.meta && <> · {entry.meta}</>}
            </Typography>
            {entry.note && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: color.gray700,
                  mt: 0.5,
                  whiteSpace: "pre-wrap",
                }}
              >
                {entry.note}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default DealTimeline;
